import { useLanguage } from '@tink/core/src/components/providers/LanguageProvider/LanguageProvider';

import { ProductType } from '@tink/core/src/types';
import { useEffect } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { Redirect, RedirectProps, Route, Switch } from 'react-router-dom';
import { ConfigurationModel } from '@tink/config/src/tinkLinkConfig';
import { useAnalytics } from '../../core/Analytics/AnalyticsProvider';
import { parentMessenger } from '../../core/Analytics/ParentApplicationEventMessenger';
import { LoadingOverlay } from '../../core/screens/LoadingOverlay/LoadingOverlay';
import {
  ProductDimension,
  ProductDimensionPay,
  ProductDimensionTransactions,
} from '../../models/product';

import { FinancialServicesSegment } from '../../models/provider';
import { UserEntity } from '../../models/user';
import { DirectPaymentInitiator } from '../../payments/legacy/components/DirectPaymentInitiator/DirectPaymentInitiator';
import { DuplicatePaymentErrorScreenWrapper } from '../../payments/DirectPayments/components/DuplicatePaymentErrorScreen/DuplicatePaymentErrorScreenWrapper';
import {
  getDescribeReportsPayload,
  isReportsProduct,
} from '../../reports/components/BundledFlow/describe-reports-payload';
import { buildHomeUrl } from '../../routes/index';
import { describeReportsWithDispatch, describeWithDispatch } from '../../services/oauth';
import { getSession as getSessionConnect } from '../../services/session';
import { setApplicationError as setApplicationErrorConnect } from '../../stores/app-state-store';
import { AppState } from '../../stores/redux-store';
import { applicationParameters } from '../../utilities/application-parameters';
import { getLanguageCode } from '../../utilities/utils';
import { useUrlQuery } from '../ApplicationProviders/UrlQueryProvider/UrlQueryProvider';
import { useVersion } from '../ApplicationProviders/VersionProvider/VersionProvider';
import {
  ConfigurationLoadingState,
  useConfiguration,
} from '../ApplicationProviders/ConfigurationProvider/ConfigurationProvider';
import { DescribeRequest } from '../../lib-api/services/Oauth';
import { getPaymentRefreshableItems } from '../../payments/helpers/fraud-prevention';

export type AppInitiatorProps = PropsFromRedux & {
  productType: ProductType;
  productDimension: ProductDimension;
  children: React.ReactNode;
  RedirectComponent?: React.ComponentType<RedirectProps>;
};

const getOauthDescribePayload = (
  productType: ProductType,
  productDimension: ProductDimension,
  config: ConfigurationModel
): DescribeRequest => {
  if (productType === ProductType.AUTHORIZE || productType === ProductType.CREDENTIALS) {
    return {
      scope: applicationParameters.get('scope'),
      state: applicationParameters.get('state'),
      clientId: applicationParameters.get('clientId'),
      redirectUri: applicationParameters.get('redirectUri'),
    };
  }

  if (productType === ProductType.PAY) {
    const refreshableItems = getPaymentRefreshableItems(productType, productDimension, config);

    return {
      refreshItems: refreshableItems,
      state: applicationParameters.get('state'),
      clientId: applicationParameters.get('clientId'),
      redirectUri: applicationParameters.get('redirectUri'),
    };
  }

  return {
    refreshItems: applicationParameters.get('refreshableItems'),
    state: applicationParameters.get('state'),
    clientId: applicationParameters.get('clientId'),
    redirectUri: applicationParameters.get('redirectUri'),
  };
};

export const AppInitiatorPresenter: React.FC<AppInitiatorProps> = ({
  children,
  productType,
  productDimension,
  isUserLoaded,
  isProvidersLoaded,
  isOauthLoaded,
  user,
  describeClient,
  describeReportsClient,
  getSession,
  RedirectComponent = Redirect,
}) => {
  const {
    version: { version },
  } = useVersion();
  const { changeLocale } = useLanguage();
  const { market, locale } = useUrlQuery();
  const { tinklinkAnalytics } = useAnalytics();
  const { loadingState: configurationLoadingState, data: config } = useConfiguration();
  const isEmbedded = window.top !== window.self;

  useEffect(() => {
    if (user) {
      getSession(applicationParameters.get('sessionId'));

      changeLocale(getLanguageCode(locale));

      tinklinkAnalytics.update({ market });
      parentMessenger.setSessionId(applicationParameters.get('trackingId'));
      parentMessenger.setIsIframe(applicationParameters.get('isIframe'));
      parentMessenger.setIsMobileSDK(applicationParameters.get('isMobileSDK'));
      parentMessenger.setIsTinkLinkEmbedded(isEmbedded);
    }
  }, [changeLocale, getSession, tinklinkAnalytics, user, market, locale, isEmbedded]);

  useEffect(() => {
    if (isUserLoaded && configurationLoadingState === ConfigurationLoadingState.LOADED) {
      if (isReportsProduct(productType)) {
        const describeReportsPayload = getDescribeReportsPayload(productType);

        describeReportsClient({
          state: applicationParameters.get('state'),
          clientId: applicationParameters.get('clientId'),
          redirectUri: applicationParameters.get('redirectUri'),
          ...describeReportsPayload,
        });
      } else {
        const payload = getOauthDescribePayload(productType, productDimension, config);
        describeClient(payload);
      }
    }
  }, [
    describeClient,
    describeReportsClient,
    productType,
    productDimension,
    configurationLoadingState,
    config,
    isUserLoaded,
  ]);

  if (
    !isUserLoaded ||
    !isProvidersLoaded ||
    !isOauthLoaded ||
    configurationLoadingState === ConfigurationLoadingState.LOADING
  ) {
    return <LoadingOverlay ariaLabel="Loading" />;
  }

  if (productType === ProductType.PAY) {
    return (
      <Switch>
        <Route exact path={`/:version/${ProductType.PAY}/${ProductDimensionPay.DIRECT}`}>
          <DirectPaymentInitiator>{children}</DirectPaymentInitiator>
        </Route>
        <Route path={`/:version/${ProductType.PAY}/${ProductDimensionPay.DIRECT}`}>
          <DuplicatePaymentErrorScreenWrapper>{children}</DuplicatePaymentErrorScreenWrapper>
        </Route>
        <Route>{children}</Route>
      </Switch>
    );
  }

  if (productType === ProductType.TRANSACTIONS) {
    if (
      (applicationParameters.get('financialServicesSegments') || []).includes(
        FinancialServicesSegment.BUSINESS
      )
    ) {
      return (
        <RedirectComponent
          to={buildHomeUrl({
            version,
            productType: ProductType.BUSINESS_TRANSACTIONS,
            productDimension: ProductDimensionTransactions.CONNECT_ACCOUNTS,
          })}
        />
      );
    }
  }

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
};

const mapStateToProps = (state: AppState) => {
  const { user: userStore, providers: providersStore, oauth: oauthStore } = state;
  const isUserLoaded = Boolean(!userStore.loading && userStore.user && userStore.accessToken);
  const isProvidersLoaded = Boolean(!providersStore.loading) && providersStore.data !== null;
  const isOauthLoaded = Boolean(oauthStore.description);

  return {
    isUserLoaded,
    isProvidersLoaded,
    isOauthLoaded,
    user: state.user.user as UserEntity,
  };
};

const mapDispatchToProps = {
  getSession: getSessionConnect,
  setApplicationError: setApplicationErrorConnect,
  describeClient: describeWithDispatch,
  describeReportsClient: describeReportsWithDispatch,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export const AppInitiator = connector(AppInitiatorPresenter);
